import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { registerLicense } from '@syncfusion/ej2-base';

import ErrorBoundary from './components/ErrorBoundary';
import { initPostHog } from './components/PostHogProvider';
import VersionUpdateBanner from './components/VersionUpdateBanner/VersionUpdateBanner';
import { lazyWithReload } from './lib/lazyWithReload';
import { resolveAppBranch } from './appBranch';

const InternalApp = lazyWithReload(() => import('./InternalApp'));
const CockpitApp = lazyWithReload(() => import('./pages/Cockpit/CockpitApp'));
const SellerApp = lazyWithReload(() => import('./pages/SellerPortal/SellerApp'));

registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE_KEY);

// PostHog is initialised here rather than inside <PostHogProvider /> because
// that component renders only on the internal branch below, and it is the app's
// only caller of posthog.init. A /cockpit/* load would otherwise register and
// capture against an uninitialised client, silently dropping every cockpit
// event. Init is idempotent and no-ops under test.
initPostHog();

// The cockpit ships as a top-level branch of the React tree mounted OUTSIDE
// the ClerkProvider and Apollo client used by the internal BID app. We
// decide which branch to render by inspecting the pathname once at mount
// time — the two trees never share providers.
//
// IMPORTANT: Cockpit is mounted as a separate Clerk-authed tree. The
// path-check below is evaluated ONCE at module load and is NOT reactive to
// in-app navigation. Navigation INTO `/cockpit` from within the internal
// app MUST be a hard navigation (e.g. `window.location.assign(paths.cockpit)`)
// so the bundle remounts and picks the cockpit branch. Do NOT use React
// Router's `<Link>` / `navigate()` to enter the cockpit — the path-check
// will not re-run and the user will fall through to the internal app's
// catch-all NotFound route. See `routes/index.tsx` for a belt-and-braces
// hard-redirect on the `/*` catch-all.
// The host decides before the path does: on vdr.bidequity.ai the whole
// surface is the seller portal (see appBranch.ts). Same non-reactive,
// once-at-module-load semantics as before.
const appBranch =
  typeof window !== 'undefined' ? resolveAppBranch(window.location.hostname, window.location.pathname) : 'internal';
const isCockpitRoute = appBranch === 'cockpit';
// Same pattern for the external Seller Portal: a second Clerk-authed tree
// at /seller (every path on the VDR hostname), mounted outside the
// internal providers. Entering it from the internal app must also be a
// hard navigation.
const isSellerRoute = appBranch === 'seller';

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        {isCockpitRoute ? (
          <BrowserRouter>
            <CockpitApp />
          </BrowserRouter>
        ) : isSellerRoute ? (
          <BrowserRouter>
            <SellerApp />
          </BrowserRouter>
        ) : (
          <>
            <VersionUpdateBanner />
            <InternalApp />
            <style>{`
            .sentry-feedback {
              display: none !important;
            }
          `}</style>
          </>
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
